// ═══════════════════════════════════════════════════════════════
// ContentSnippetSaveModal.tsx – a bejegyzés tartalmának mentése tartalom-változóként (csak superadmin)
//
// A szerver a forrásszervezet ismert adatait (név, cím, telefon…) {{KULCS}} helyőrzővé alakítja vissza;
// ez itt ellenőrizhető és javítható. Beszúráskor a célszervezet adataival töltődik ki.
// Formázott tartalomnál a stílusok is megmaradnak (a formázott szerkesztőbe beszúrva).
// ═══════════════════════════════════════════════════════════════
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ApiClient } from '../../api/client';
import { invalidateContentSnippets } from '../../hooks/useContentSnippets';
import { RichTextView } from './RichTextView';
import { X, Save, RefreshCw, Globe, Building2, AlertCircle, Braces } from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  tenantId?: number;
  defaultName?: string;
  text: string;
  html?: string;
  onSaved?: (name: string) => void;
}

export const ContentSnippetSaveModal: React.FC<Props> = ({ isOpen, onClose, tenantId, defaultName = '', text, html = '', onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'global' | 'tenant'>('global');
  const [tText, setTText] = useState('');
  const [tHtml, setTHtml] = useState('');
  const [replaced, setReplaced] = useState<{ key: string; count: number }[]>([]);
  const [source, setSource] = useState<{ id: number; name: string } | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setName(defaultName.slice(0, 120)); setScope('global'); setError(null); setLoading(true);
    ApiClient.prepareContentSnippet({ tenant_id: tenantId, text, html: html || undefined }).then((r) => {
      if (r.ok) { setTText(r.text || ''); setTHtml(r.html || ''); setReplaced(r.replaced || []); setSource(r.source_tenant || null); }
      else setError(r.error || 'A tartalom nem dolgozható fel');
    }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const save = async () => {
    setSaving(true); setError(null);
    const r = await ApiClient.createContentSnippet({ name, scope, tenant_id: tenantId, text: tText, html: tHtml || undefined });
    setSaving(false);
    if (!r.ok) { setError(r.error || 'A mentés nem sikerült'); return; }
    invalidateContentSnippets();
    onSaved?.(name);
    onClose();
  };

  return createPortal(
    <div data-esc-self className="fixed inset-0 z-[10002] flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }}>
      <div role="dialog" aria-modal="true" aria-labelledby="snip-title" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col my-auto max-h-[90vh]">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-start justify-between gap-3">
          <div>
            <h2 id="snip-title" className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2"><Braces className="w-5 h-5 text-violet-600" /> Mentés tartalom-változóként</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              A teljes tartalom újrahasznosítható blokk lesz a Változó listában. A {source ? `„${source.name}”` : 'szervezet'} adatai helyőrzővé alakulnak, beszúráskor mindig az adott szervezet adatai kerülnek be.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Bezárás" className="p-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-white rounded-lg cursor-pointer"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-4 text-xs">
          {error && <div role="alert" className="p-2.5 rounded-lg bg-rose-50 text-rose-800 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800 flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}
          {loading ? (
            <div className="py-10 flex justify-center text-slate-400"><RefreshCw className="w-5 h-5 animate-spin" /></div>
          ) : (
            <>
              <div>
                <label htmlFor="snip-name" className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Név (ez jelenik meg a Változó listában)</label>
                <input id="snip-name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white" />
              </div>
              <fieldset>
                <legend className="font-semibold text-slate-700 dark:text-slate-300 mb-1">Hol legyen elérhető?</legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {([['global', 'Globálisan', 'minden szervezetnél', Globe], ['tenant', 'Csak ennél a szervezetnél', source?.name || '', Building2]] as const).map(([v, l, d, Icon]) => (
                    <label key={v} className={`p-2.5 rounded-lg border cursor-pointer flex items-start gap-2 ${scope === v ? 'border-violet-500 bg-violet-50/60 dark:bg-violet-950/20' : 'border-slate-200 dark:border-slate-700'}`}>
                      <input type="radio" name="snip-scope" checked={scope === v} onChange={() => setScope(v)} className="mt-0.5" />
                      <span><span className="font-semibold text-slate-900 dark:text-white flex items-center gap-1"><Icon className="w-3.5 h-3.5" />{l}</span><span className="text-[11px] text-slate-500">{d}</span></span>
                    </label>
                  ))}
                </div>
              </fieldset>
              <div>
                <div className="font-semibold text-slate-700 dark:text-slate-300 mb-1">Helyőrzővé alakítva</div>
                {replaced.length ? (
                  <div className="flex flex-wrap gap-1">{replaced.map((r) => <span key={r.key} className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300 font-mono text-[10px]">{`{{${r.key}}}`} ×{r.count}</span>)}</div>
                ) : <p className="text-slate-500">Nem volt benne a szervezet adataiból – a tartalom szó szerint kerül mentésre.</p>}
              </div>
              <div>
                <label htmlFor="snip-text" className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Szöveg (egyszerű beszúráshoz – javítható)</label>
                <textarea id="snip-text" rows={7} value={tText} onChange={(e) => setTText(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-lg font-mono text-[11px] text-slate-900 dark:text-white resize-y" />
              </div>
              {tHtml && (
                <div>
                  <div className="font-semibold text-slate-700 dark:text-slate-300 mb-1">Formázott változat (a formázott szerkesztőbe a stílusokkal együtt kerül)</div>
                  <div className="max-h-56 overflow-auto p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950"><RichTextView html={tHtml} /></div>
                </div>
              )}
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl cursor-pointer">Mégse</button>
          <button type="button" onClick={save} disabled={saving || loading || !name.trim() || (!tText.trim() && !tHtml)}
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl text-xs font-bold text-white bg-violet-600 hover:bg-violet-500 cursor-pointer disabled:opacity-40">
            {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Mentés
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ContentSnippetSaveModal;
